import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatError } from "../utils/api";
import Navbar from "../components/Navbar";
import DocHelpButton from "../components/DocHelpButton";
import VsmWizard from "../components/wizard/VsmWizard";
import FiveSWizard from "../components/wizard/FiveSWizard";
import KaizenWizard from "../components/wizard/KaizenWizard";
import SipocWizard from "../components/wizard/SipocWizard";
import VocKanoWizard from "../components/wizard/VocKanoWizard";
import TimeStudyWizard from "../components/wizard/TimeStudyWizard";
import WasteWalkWizard from "../components/wizard/WasteWalkWizard";
import FiveWhysFishboneWizard from "../components/wizard/FiveWhysFishboneWizard";
import ParetoWizard from "../components/wizard/ParetoWizard";
import MaturityWizard from "../components/wizard/MaturityWizard";
import SpaghettiWizard from "../components/wizard/SpaghettiWizard";
import PFMEAWizard from "../components/wizard/PFMEAWizard";
import A3Wizard from "../components/wizard/A3Wizard";
import DmaicWizard from "../components/wizard/DmaicWizard";
import YamazumiWizard from "../components/wizard/YamazumiWizard";
import ImpactEffortWizard from "../components/wizard/ImpactEffortWizard";
import {
  RoiWizard, IdeaBankWizard, StandardWorkWizard, PokaYokeWizard, KanbanWizard, SmedWizard,
  KpiScorecardWizard, LswWizard, LpaWizard, TnaWizard, AcademyWizard, TierMeetingWizard,
  AttritionWizard, AutomationScorerWizard, DiloWizard,
} from "../components/wizard/AllWizards";
import {
  LeanWaveWizard, BenefitTrackerWizard, IluoWizard, AdkarWizard, ErlangCWizard, MonteCarloWizard, CustomerJourneyWizard,
} from "../components/wizard/BespokeWizards";
import { exportVsmPDF, exportFiveSPDF, exportKaizenPDF, exportSipocPDF, exportVocPDF, exportTimeStudyPDF, exportWasteWalkPDF, exportRcaPDF, exportParetoPDF, exportMaturityPDF, exportSpaghettiPDF, exportPFMEAPDF, exportA3PDF, exportDmaicPDF, exportYamazumiPDF, exportImpactEffortPDF } from "../utils/exportToolPDF";
import { makeExporter } from "../utils/exportGenericPDF";
import { ChevronLeft } from "lucide-react";

const WIZARDS = {
  vsm_builder:        { component: VsmWizard,             name: "Value Stream Mapping",          pdf: exportVsmPDF },
  five_s_audit:       { component: FiveSWizard,           name: "5S Workplace Audit",            pdf: exportFiveSPDF },
  kaizen_charter:     { component: KaizenWizard,          name: "Kaizen Event Charter",          pdf: exportKaizenPDF },
  sipoc_mapper:       { component: SipocWizard,           name: "SIPOC Mapper",                  pdf: exportSipocPDF },
  voc_kano:           { component: VocKanoWizard,         name: "Voice of Customer + Kano",      pdf: exportVocPDF },
  time_study:         { component: TimeStudyWizard,       name: "Process Observation / Time Study", pdf: exportTimeStudyPDF },
  waste_walk:         { component: WasteWalkWizard,       name: "Waste Walk / Gemba Tracker",    pdf: exportWasteWalkPDF },
  rca_5whys_fishbone: { component: FiveWhysFishboneWizard,name: "5 Whys + Fishbone RCA",         pdf: exportRcaPDF },
  pareto_analyzer:    { component: ParetoWizard,          name: "Pareto Analyzer",               pdf: exportParetoPDF },
  maturity_assessment:{ component: MaturityWizard,        name: "Lean Maturity Assessment",      pdf: exportMaturityPDF },
  spaghetti_diagram:  { component: SpaghettiWizard,       name: "Spaghetti / Flow Diagram",      pdf: exportSpaghettiPDF },
  pfmea:              { component: PFMEAWizard,           name: "PFMEA",                         pdf: exportPFMEAPDF },
  a3_problem_solving: { component: A3Wizard,              name: "A3 Problem Solving",            pdf: exportA3PDF },
  dmaic_charter:      { component: DmaicWizard,           name: "DMAIC Project Charter",         pdf: exportDmaicPDF },
  yamazumi:           { component: YamazumiWizard,        name: "Takt / Cycle / Yamazumi",       pdf: exportYamazumiPDF },
  impact_effort:      { component: ImpactEffortWizard,    name: "Impact × Effort Prioritisation", pdf: exportImpactEffortPDF },
  // C — Implement
  lean_wave_charter:  { component: LeanWaveWizard,        name: "Lean Wave Programme Charter",   pdf: makeExporter("wave") },
  roi_calculator:     { component: RoiWizard,             name: "Benefit Case / ROI",            pdf: makeExporter("roi") },
  idea_bank:          { component: IdeaBankWizard,        name: "Initiative / Idea Bank",        pdf: makeExporter("ideabank") },
  standard_work:      { component: StandardWorkWizard,    name: "Standard Work (SWD)",           pdf: makeExporter("swd") },
  poka_yoke:          { component: PokaYokeWizard,        name: "Poka-Yoke Catalogue",           pdf: makeExporter("pokayoke") },
  kanban_designer:    { component: KanbanWizard,          name: "Kanban / Pull System",          pdf: makeExporter("kanban") },
  smed:               { component: SmedWizard,            name: "SMED",                          pdf: makeExporter("smed") },
  // D — Sustain
  kpi_scorecard:      { component: KpiScorecardWizard,    name: "KPI / OpEx Scorecard",          pdf: makeExporter("kpi") },
  tier_meeting:       { component: TierMeetingWizard,     name: "Tier Meeting Designer",         pdf: makeExporter("tiermtg") },
  leader_standard_work: { component: LswWizard,           name: "Leader Standard Work",          pdf: makeExporter("lsw") },
  lpa_scheduler:      { component: LpaWizard,             name: "Layered Process Audit",         pdf: makeExporter("lpa") },
  benefit_tracker:    { component: BenefitTrackerWizard,  name: "Benefit Realisation Tracker",   pdf: makeExporter("bt") },
  // E — People
  iluo_matrix:        { component: IluoWizard,            name: "ILUO Skills Matrix",            pdf: makeExporter("iluo") },
  tna:                { component: TnaWizard,             name: "Training Needs Analysis",       pdf: makeExporter("tna") },
  adkar:              { component: AdkarWizard,           name: "Change Readiness (ADKAR)",      pdf: makeExporter("adkar") },
  lean_academy:       { component: AcademyWizard,         name: "Lean Academy",                  pdf: makeExporter("academy") },
  // F — Domain
  erlang_c:           { component: ErlangCWizard,         name: "Erlang C Sizing",               pdf: makeExporter("erlang") },
  monte_carlo_queue:  { component: MonteCarloWizard,      name: "Monte Carlo Queue",             pdf: makeExporter("mc") },
  attrition_cohort:   { component: AttritionWizard,       name: "Attrition Cohort",              pdf: makeExporter("attrition") },
  automation_scorer:  { component: AutomationScorerWizard,name: "Automation Scorer",             pdf: makeExporter("automation") },
  customer_journey:   { component: CustomerJourneyWizard, name: "Customer Journey",              pdf: makeExporter("cj") },
  // DILO
  dilo:               { component: DiloWizard,            name: "Day In the Life Of",            pdf: makeExporter("dilo") },
};

export default function ToolWorkspacePage() {
  const { companyId, projectId } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [project, setProject] = useState(null);
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [c, p, b] = await Promise.all([
          api.get(`/companies/${companyId}`),
          api.get(`/projects/${projectId}`),
          api.get(`/my/branding`).catch(() => ({ data: { branding: null } })),
        ]);
        setCompany(c.data);
        setProject(p.data);
        setBranding(b.data?.branding || null);
        // If this is actually a staffing project, redirect to main workspace
        if (p.data.tool_type === "staffing_assessment") {
          navigate(`/workspace/${companyId}/project/${projectId}`, { replace: true });
        }
      } catch (e) {
        setError(formatError(e));
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, projectId]);

  const save = async (patch) => {
    const res = await api.put(`/projects/${projectId}/tool-data`, patch);
    setProject(res.data);
  };

  const exportPDF = async () => {
    const cfg = WIZARDS[project.tool_type];
    if (!cfg) return;
    await cfg.pdf({ project, company, branding });
  };

  if (loading) return <div className="min-h-screen bg-cream"><Navbar /><div className="text-center pt-16 text-sage-500">Loading…</div></div>;
  if (error) return <div className="min-h-screen bg-cream"><Navbar /><div className="max-w-lg mx-auto mt-10 bg-red-50 border border-red-200 p-5 rounded text-red-700">{error}</div></div>;

  const cfg = WIZARDS[project?.tool_type];
  if (!cfg) return <div className="min-h-screen bg-cream"><Navbar /><div className="max-w-lg mx-auto mt-10 text-center text-sage-500">Unknown tool type: {project?.tool_type}</div></div>;

  const Wizard = cfg.component;

  return (
    <div className="min-h-screen bg-cream">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 py-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <Link to={`/workspace/${companyId}`} className="text-xs text-sage-500 hover:text-sage-700 flex items-center gap-1"><ChevronLeft className="w-3 h-3" /> {company?.name}</Link>
            <p className="text-xs font-semibold uppercase tracking-wider text-sage-500 mt-1">{cfg.name}</p>
            <h1 className="font-heading text-xl font-semibold text-sage-900">{project.name}</h1>
          </div>
          <DocHelpButton toolId={project.tool_type} label="Read docs" />
        </div>
        <Wizard project={project} company={company} branding={branding} onSave={save} onExportPDF={exportPDF} />
      </main>
    </div>
  );
}
